import React, { useContext, useState } from 'react';
import { CheckOutlined, CloseOutlined, EditOutlined } from '@ant-design/icons';
import {
  Button,
  Flex,
  Input,
  theme as antTheme,
  Tooltip,
  Typography,
} from 'antd';

import DeviceContext from '@totem/components/devices/deviceDetails/DeviceContext';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { DEVICES_ENDPOINT } from '@totem/utilities/endpoints';
import { CheckResponseShowError } from '@totem/utilities/responseUtilities';

const DeviceNameEditor = () => {
  const { userProfile, getApiToken } = useContext(UserProfileContext);
  const { deviceData, onAction } = useContext(DeviceContext);
  const { token } = antTheme.useToken();
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [isSending, setIsSending] = useState<boolean>(false);
  const [name, setName] = useState<string>('');

  const canEdit = userProfile.organization.role >= 2;
  const displayName = deviceData?.device.displayName ?? '';
  const hostname = (deviceData?.device.hostname ?? '').trim();

  const startEditing = () => {
    setName(displayName);

    setIsEditing(true);
  };

  const save = () => {
    const trimmed = name.trim();
    if (isSending || trimmed === '') {
      return;
    }
    if (trimmed === displayName) {
      setIsEditing(false);
      return;
    }

    setIsSending(true);

    fetch(`${DEVICES_ENDPOINT}/${deviceData.device.id}/displayName`, {
      method: 'PUT',
      headers: new Headers({
        Authorization: `Bearer ${getApiToken()}`,
      }),
      body: JSON.stringify({ displayName: trimmed }),
    }).then((res) => {
      setIsSending(false);
      if (CheckResponseShowError(res)) {
        setIsEditing(false);

        onAction('refresh');
      }
    });
  };

  if (isEditing) {
    return (
      <Flex gap={4} align="center">
        <Input
          autoFocus
          value={name}
          maxLength={200}
          disabled={isSending}
          onChange={(event) => setName(event.target.value)}
          onPressEnter={save}
          onKeyDown={(event) => {
            if (event.key === 'Escape') {
              setIsEditing(false);
            }
          }}
        />
        <Tooltip title="Save">
          <Button
            type="text"
            icon={<CheckOutlined />}
            loading={isSending}
            disabled={name.trim() === ''}
            onClick={save}
          />
        </Tooltip>
        <Tooltip title="Cancel">
          <Button
            type="text"
            icon={<CloseOutlined />}
            disabled={isSending}
            onClick={() => setIsEditing(false)}
          />
        </Tooltip>
      </Flex>
    );
  }

  return (
    <Flex vertical align="center">
      <Flex gap={4} align="center">
        <span>{displayName}</span>
        {canEdit && (
          <Tooltip title="Rename device">
            <Button
              type="text"
              size="small"
              icon={<EditOutlined />}
              onClick={startEditing}
            />
          </Tooltip>
        )}
      </Flex>
      {hostname !== '' && hostname !== displayName && (
        <Tooltip title="Hostname reported by the device's tools">
          <Typography.Text
            type="secondary"
            style={{ fontSize: token.fontSizeSM }}
          >
            ({hostname})
          </Typography.Text>
        </Tooltip>
      )}
    </Flex>
  );
};

export default DeviceNameEditor;
